import {
  ref, computed, watch, onMounted, nextTick,
} from 'vue';
import { useCached, provideLocal, injectLocal } from '@vueuse/core';
import { useUpc } from '@common-vue/media-card-large';
import { post } from '@utils/api';
import { useEnvProps } from '@composables/useEnvProps';
import { isEmpty, formatPoints } from '@utils/commonUtils';
import { PdpUserStatus } from '@utils/pdpUserStatus';
import { useQuantity, useProduct } from '@modules/product';
import { useFeatureEligibility } from '@modules/featureControl';
import { useLoyalty } from '@composables/useLoyalty';
import { useUser } from '@composables/user/useUser';
import { getLowestPrice } from '@modules/price/parsers';
import { useProductPrice } from '@modules/price/composables';
import {
  LOYALTY_HEADERS, REGEX_TIERS_NAMES,
} from 'config';
import { LOYALTY_3PC_OFFER_NAMES, LOYALTY_TIER_DETAILS, PPC_DATA } from '@modules/ppc/constants';

import { ppcMapper } from '../mappers';
import { isValidPayload as isValidPayloadUtil } from '../utils';

// @returns (mcom) -> /BRONZE|SILVER|GOLD|PLATINUM/gi
// @returns (bcom) -> /BASE|TOL|UNL/gi
const TIERS_REGEX_EXPRESSION = new RegExp(REGEX_TIERS_NAMES.join('|'), 'gi');

/**
 * @returns {UsePpcResponse}
 */
export function usePpc() {
  const ppcData = injectLocal(PPC_DATA);
  if (typeof ppcData === 'undefined') {
    throw new TypeError(
      'usePpc() is called without provider.',
    );
  }

  return ppcData;
}

export function usePpcProvide() {
  const { isPpcEnabled, isProductPerksEnabled } = useFeatureEligibility();
  const { hostlink, isBcom } = useEnvProps();
  const PPC_URL = `${hostlink}/xapi/loyalty/v1/prepurchasepoints?_origin=pdp`;

  const cachedPrice = useCached(ref(1));
  const cachedQuantity = useCached(ref(1));

  const { quantity } = useQuantity();
  const { productId } = useProduct();
  const {
    userGuid, userId, bagGuid,
  } = useUser();
  const {
    tierName, encryptedLoyaltyId, defaultTenderType, isUserNonLoyalist,
  } = useLoyalty();
  const { upcData, selectedUpcId } = useUpc(productId);
  const selectedPrice = useProductPrice();

  const ppcProps = ref({});
  const modalProps = ref({});
  const ppcIconName = ref('');
  const isPpcAvailable = ref(false);
  const isBaseTier = ref(false);
  const isMultiplierOrPointsPerDollar = ref(false);
  const ppcLoading = ref(null);

  const isPpcLoading = computed({
    get() {
      return ppcLoading.value !== null
        ? ppcLoading.value
        : !isUserNonLoyalist.value && isPpcEnabled && selectedPrice.value?.offersData?.topOffer?.isLoyallistOffer;
    },
    set(value) {
      ppcLoading.value = value;
    },
  });

  function _getPpcAvailability(parsedPoints) {
    const parsedPointsDefined = parsedPoints > 0;

    if (isBcom) {
      return parsedPointsDefined && (PdpUserStatus.is365Customer() || PdpUserStatus.isSignedIn() || PdpUserStatus.isSoftSignedIn());
    }

    return parsedPointsDefined;
  }

  function _isBonusDayEligible() {
    const { offers } = selectedPrice.value;
    const activeOffers = offers?.lists?.activeOffers || /* istanbul ignore next */ [];
    const result = activeOffers.filter((element) => element.header === 'Earn Bonus Points NOW');
    return result.length !== 0;
  }

  function _isPpcOfferToHideLoyaltyBadge(items = []) {
    const OFFER_LIST = [LOYALTY_3PC_OFFER_NAMES.multiplier, LOYALTY_3PC_OFFER_NAMES.pointsPerDollar];
    const offersArr = items[0]?.offerDetails ?? [];
    // find first offer of offer types and return if its MULTIPLIER or PULSE
    return !!offersArr.find((offer) => OFFER_LIST.includes(offer.offerType));
  }

  /**
   * @param {import('../types').Response} response
   * @param {boolean} [forcedAvailable=false]
   * @returns {void}
   * */
  function _updatePpcData({ points }, forcedAvailable = false) {
    const parsedPendingPoints = parseInt(points.totalPendingPoints, 10);

    isPpcAvailable.value = forcedAvailable || _getPpcAvailability(parsedPendingPoints);

    const pointsPerItems = points.items;
    if (isBcom && isProductPerksEnabled) {
      isBaseTier.value = !pointsPerItems?.[0]?.offerDetails;
    }
    isMultiplierOrPointsPerDollar.value = _isPpcOfferToHideLoyaltyBadge(pointsPerItems);
    // update ppc props
    Object.assign(ppcProps.value, {
      'pending-points': formatPoints(parsedPendingPoints),
      ...(isBcom
        ? { 'points-per-item': pointsPerItems }
        : { 'is-bonus-day-badge': _isBonusDayEligible(), 'is-bonus-points-badge': +points.bonusPoints > 0 }
      ),
    });
  }

  function _findLoyaltyTiersDetails() {
    if (isUserNonLoyalist.value) return;

    const tierMatches = tierName.value.match(TIERS_REGEX_EXPRESSION) ?? [];
    const [tier = ''] = tierMatches;

    const formattedTier = (tier.length ? tier : tierName.value).toLowerCase();

    const { icon = '', membershipDetails = {}, modalDetails = {} } = LOYALTY_TIER_DETAILS[formattedTier] ?? {};

    ppcIconName.value = icon;
    modalProps.value = modalDetails;
    if (isBcom) {
      Object.assign(modalProps.value, { 'membership-details': membershipDetails });
    }
  }

  function _changeCachedQuantity() {
    cachedQuantity.value = quantity.value;
  }

  function _changeCachedPrice() {
    cachedPrice.value = getLowestPrice(selectedPrice.value);
  }

  /**
   * @param {import('../types').Response} mockResponse
   * @param {boolean} [forcedAvailable=false]
   * @returns {Promise<void>}
   * */
  async function fetchPpcData({ mockResponse = null, forcedAvailable = false } = {}) {
    if (isUserNonLoyalist.value) return;
    if (mockResponse) {
      isPpcLoading.value = false;
      _updatePpcData(mockResponse, forcedAvailable);
      return;
    }

    const { requiredPayload, fullPayload } = ppcMapper({
      upcData: upcData.value,
      selectedUpcId: selectedUpcId.value,
      selectedPrice: selectedPrice.value,
      quantity: quantity.value,
      userGUID: userGuid.value,
      userId: userId.value,
      bagGuid: bagGuid.value,
      tier: tierName.value.toUpperCase(),
      encryptedLoyaltyId: encryptedLoyaltyId.value,
      tenderType: defaultTenderType.value,
    });
    const isValidPayload = isValidPayloadUtil(requiredPayload);

    if (isValidPayload && quantity.value >= 1) {
      try {
        const response = await post(PPC_URL, fullPayload, { headers: LOYALTY_HEADERS });

        if (isEmpty(response)) throw new Error('PPC response is empty');

        _updatePpcData(response);
      } catch (e) {
        throw new Error(`PPC request failed: ${e}`);
      } finally {
        isPpcLoading.value = false;
      }
    } else {
      isPpcLoading.value = false;
      throw new Error(`PPC payload is incorrect or quantity < 1: payload: ${fullPayload}, quantity: ${quantity.value}`);
    }
  }

  watch(quantity, _changeCachedQuantity, { immediate: true });
  watch(selectedPrice, _changeCachedPrice, { deep: true, immediate: true });
  // updates props with appropriate tier specific data
  watch(tierName, _findLoyaltyTiersDetails);

  if (isPpcEnabled) {
    watch([cachedQuantity, cachedPrice], fetchPpcData);
    onMounted(async () => {
      // will be called once User and Loyalty data have been processed
      await nextTick(fetchPpcData);
    });
  }

  const isShowPpc = computed(() => isPpcEnabled && isPpcAvailable.value && !isUserNonLoyalist.value);

  provideLocal(PPC_DATA, {
    fetchPpcData,
    ppcProps,
    modalProps,
    ppcIconName,
    isBaseTier,
    isMultiplierOrPointsPerDollar,
    isShowPpc,
    isPpcLoading,
  });
}
